import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { t } from '../../../lib/i18n';
import { remindInvoice } from '../../deals/api';
import { FINANCE_CLIENTS_QUERY_KEY, financeErrorMessage, refusalCode } from '../api';
import { clientDueText, invoiceNumberLabel, invoiceWhatLabel, tierLabel } from '../labels';
import type { FinanceClient } from '../types';
import {
  CLIENT_STATUS_CHIP,
  CLIENT_STATUS_STRIPE,
  INVOICE_STATUS_TEXT,
  chfOrDash,
  clientInitials,
  clientStatusKey,
  clientTotals,
  cn,
  formatChf,
  formatDayMonth,
  invoicePaid,
  invoiceStatus,
  invoiceStatusKey,
  isProvisionalInvoiceNo,
  owedTextClass,
  recentReminder,
  remindTarget,
  rowAction,
} from '../utils';

interface FinanceClientRowProps {
  client: FinanceClient;
  expanded: boolean;
  onToggle: () => void;
}

/**
 * The desktop grid template, repeated by the table header and every row.
 * The six figure columns are sized to their widest real content (a 13px mono
 * «CHF 1 632,14» ≈ 93px, the 14px owed figure, «ЧАСТКОВО СПЛАЧЕНО» ≈ 135px,
 * the «Нагадати» button ≈ 80px) so the client column — name and email, the
 * one thing an operator scans the table by — keeps the rest of the row.
 */
export const CLIENT_COLUMNS =
  'grid-cols-[minmax(0,1fr)_96px_96px_96px_108px_140px_92px] gap-3 px-[18px]';

/**
 * One client's balance — `Redesign-D-Admin{,-Mobile}`, «Фінанси».
 *
 * Desktop is a seven-column grid row that expands into the client's invoices;
 * mobile is a card carrying the same facts in three stacked bands, because a
 * seven-column grid at 390px is a horizontal scroll nobody reads.
 *
 * ── Every column is CHF, and the deposit still stands apart ─────────────────
 * Invoices are CHF (owner rule 01) and `depositChf` is `users.biddingPower`,
 * CHF at the source. One unit, but the deposit is the client's own money held
 * against bids, not something billed or paid — it is never summed with the
 * invoice columns beside it.
 *
 * ── Two things are DERIVED, not carried ─────────────────────────────────────
 * An invoice's display status (`overdue` on top of the stored
 * `pending|partial|checking|paid` ladder) comes from `deriveInvoiceStatus` in
 * `@carsfinders/contracts` — the same function the API's own rollup calls, so
 * the screen and the server cannot disagree about what "past due" means. The
 * deadline phrase under the status chip comes from `nextDueAt` against now.
 * Both used to be strings on the wire and both were stale the moment they were
 * cached.
 *
 * ── A `PROV-` row is not a quotable invoice ─────────────────────────────────
 * The rollup emits `PROV-<dealId>` for a receivable derived from an invoiced
 * deal: no such document exists, nothing can be paid against that string, and
 * its deadline is the platform's internal SLA clock rather than a term anyone
 * agreed to. The number cell prints «орієнтовно» instead, so an operator cannot
 * read one out to a client.
 *
 * ── «Нагадати» writes for real ──────────────────────────────────────────────
 * It posts `POST /admin/finance/invoices/:no/remind` for the invoice
 * {@link remindTarget} picks (the earliest-due open document; never a `PROV-`
 * row). The server queues an outbox row — delivery needs
 * `NOTIFY_DISPATCH_ENABLED` — and throttles to one reminder per invoice per
 * 24 h, so an invoice whose `remindedAt` is inside that window shows
 * «нагадано …» instead of the button. Both render sites (desktop, mobile) share
 * one mutation.
 */
export function FinanceClientRow({
  client,
  expanded,
  onToggle,
}: FinanceClientRowProps) {
  const totals = clientTotals(client);
  const owes = totals.owed > 0;
  const action = rowAction(client.status);
  const remind = useRemindAction(client);
  const statusLabel = t(clientStatusKey(client.status));
  const initials = clientInitials(client.name);
  const dueText = clientDueText(client);
  const subtitle = [tierLabel(client.tier), client.depositNote].filter(Boolean).join(' · ');

  const avatarClass = owes ? 'bg-cf-brand text-white' : 'bg-cf-fill text-cf-ink-muted';
  const depositClass =
    client.status === 'refund'
      ? 'text-cf-info-ink'
      : client.depositChf
        ? 'text-cf-ink'
        : 'text-cf-ink-subtle';
  const owedClass = owedTextClass(client.status, totals.owed);
  const dueClass = client.status === 'overdue' ? 'text-cf-danger' : 'text-cf-ink-subtle';
  const actionLabel = t('adminFinance.table.remind');

  /** Invoices, or the "nothing invoiced" line the artboard prints instead. */
  const invoiceList = client.invoices.length ? (
    client.invoices.map((invoice) => {
      const status = invoiceStatus(invoice);
      const provisional = isProvisionalInvoiceNo(invoice.no);
      const due = formatDayMonth(invoice.dueAt);
      return (
        <div key={invoice.no}>
          {/* Desktop: five columns, numbers right-aligned so they compare. */}
          <div className="hidden grid-cols-[130px_minmax(0,1fr)_110px_110px_130px] items-center gap-3.5 border-b border-cf-line-soft py-2 text-[12px] lg:grid">
            <span
              className={cn('font-mono', provisional ? 'text-cf-ink-subtle italic' : 'text-cf-ink-muted')}
              title={provisional ? t('adminFinance.table.provisionalHint') : undefined}
            >
              {invoiceNumberLabel(invoice.no)}
            </span>
            <span className="min-w-0 truncate">{invoiceWhatLabel(invoice)}</span>
            <span className="text-right font-mono">{formatChf(invoice.totalChf)}</span>
            <span className="text-right font-mono text-cf-success">
              {chfOrDash(invoicePaid(invoice))}
            </span>
            <span className={`font-mono ${INVOICE_STATUS_TEXT[status]}`}>
              {t(invoiceStatusKey(status))} · {due}
            </span>
          </div>

          {/* Mobile: what it is, then what it costs. */}
          <div className="flex items-start justify-between gap-2 border-b border-cf-line-soft py-2 text-[12px] lg:hidden">
            <div className="min-w-0">
              <div className="truncate">{invoiceWhatLabel(invoice)}</div>
              <div className={`font-mono text-[10px] ${INVOICE_STATUS_TEXT[status]}`}>
                {invoiceNumberLabel(invoice.no)} · {t(invoiceStatusKey(status))} · {due}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="font-mono">{formatChf(invoice.totalChf)}</div>
              <div className="font-mono text-[10px] text-cf-success">
                {chfOrDash(invoicePaid(invoice))}
              </div>
            </div>
          </div>
        </div>
      );
    })
  ) : (
    <p className="my-1.5 text-[12px] text-cf-ink-subtle">
      {t('adminFinance.table.noInvoices', {
        deposit: chfOrDash(client.depositChf),
        note: client.depositNote ?? t('adminFinance.table.noDepositNote'),
      })}
    </p>
  );

  return (
    <>
      {/* ── Desktop row ─────────────────────────────────────────────────── */}
      <div
        className={cn(
          'hidden min-w-[960px] border-b border-cf-line-soft lg:block',
          CLIENT_STATUS_STRIPE[client.status],
        )}
      >
        <div className={cn('grid items-center py-3 text-[13px]', CLIENT_COLUMNS)}>
          {/*
            The NAME opens the client's page; the chevron beside it expands
            the invoices — so a click on the name never toggles the row too.
          */}
          <div className="flex min-w-0 items-center gap-3 text-left">
            <span
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold',
                avatarClass,
              )}
            >
              {initials}
            </span>
            <span className="min-w-0">
              <span className="flex min-w-0 items-center gap-1 text-[14px] font-semibold">
                <ClientNameLink client={client} />
                <ToggleChevron name={client.name} expanded={expanded} onToggle={onToggle} />
              </span>
              <span className="block truncate text-[12px] text-cf-ink-subtle">{subtitle}</span>
            </span>
          </div>

          <span className={cn('text-right font-mono', depositClass)}>
            {chfOrDash(client.depositChf)}
          </span>
          <span className="text-right font-mono text-cf-ink-muted">
            {chfOrDash(totals.invoiced)}
          </span>
          <span className="text-right font-mono text-cf-success">{chfOrDash(totals.paid)}</span>
          <span className={cn('text-right font-mono text-[14px] font-medium', owedClass)}>
            {chfOrDash(totals.owed)}
          </span>

          <div className="min-w-0">
            <span
              className={cn(
                'inline-flex rounded-cf-sm px-2 py-1 font-mono text-[10px] tracking-[0.08em] whitespace-nowrap uppercase',
                CLIENT_STATUS_CHIP[client.status],
              )}
            >
              {statusLabel}
            </span>
            <div className={cn('mt-1 font-mono text-[11px]', dueClass)}>{dueText}</div>
          </div>

          <div className="flex flex-col items-end gap-1 text-right">
            {action === 'remind' && <RemindControl remind={remind} label={actionLabel} size="h-8" />}
          </div>
        </div>

        {expanded && (
          <div className="border-t border-cf-line-soft bg-cf-surface-sunken pt-1.5 pr-[18px] pb-2.5 pl-[66px]">
            {invoiceList}
          </div>
        )}
      </div>

      {/* ── Mobile card ─────────────────────────────────────────────────── */}
      <article
        className={cn(
          'overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface lg:hidden',
          CLIENT_STATUS_STRIPE[client.status],
        )}
      >
        {/*
          The header still expands on a tap anywhere EXCEPT the name, which is a
          link to the client's page and stops the tap from toggling too.
        */}
        <div
          onClick={onToggle}
          className="flex w-full cursor-pointer items-center gap-2.5 px-3.5 py-3 text-left"
        >
          <span
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold',
              avatarClass,
            )}
          >
            {initials}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex min-w-0 text-[14px] font-semibold">
              <ClientNameLink client={client} />
            </span>
            <span className="block truncate text-[11px] text-cf-ink-subtle">{subtitle}</span>
          </span>
          <span className="shrink-0 text-right">
            <span className={cn('block font-mono text-[15px] font-medium', owedClass)}>
              {chfOrDash(totals.owed)}
            </span>
            <span className={cn('block font-mono text-[10px]', dueClass)}>{dueText}</span>
          </span>
          <ToggleChevron name={client.name} expanded={expanded} onToggle={onToggle} />
        </div>

        <div className="grid grid-cols-3 gap-2 border-t border-cf-line-soft bg-cf-surface-sunken px-3.5 pt-2 pb-2.5">
          <MobileFact label={t('adminFinance.table.depositChf')}>
            <span className={depositClass}>{chfOrDash(client.depositChf)}</span>
          </MobileFact>
          <MobileFact label={t('adminFinance.table.invoiced')}>
            {chfOrDash(totals.invoiced)}
          </MobileFact>
          <MobileFact label={t('adminFinance.table.paid')}>
            <span className="text-cf-success">{chfOrDash(totals.paid)}</span>
          </MobileFact>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-cf-line-soft px-3.5 py-2.5">
          <span
            className={cn(
              'inline-flex rounded-cf-sm px-[7px] py-[3px] font-mono text-[9px] tracking-[0.08em] uppercase',
              CLIENT_STATUS_CHIP[client.status],
            )}
          >
            {statusLabel}
          </span>
          {action === 'remind' && (
            <div className="flex min-w-0 flex-1 flex-col items-end gap-1 text-right">
              <RemindControl remind={remind} label={actionLabel} size="h-9" />
            </div>
          )}
        </div>

        {expanded && (
          <div className="border-t border-cf-line-soft px-3.5 pt-1 pb-2">{invoiceList}</div>
        )}
      </article>
    </>
  );
}

/* ─────────────────────────── Name and chevron ──────────────────────────────── */

/** The client's name as a link to their page — the same route «Клієнти» opens. */
function ClientNameLink({ client }: { client: FinanceClient }) {
  return (
    <Link
      to="/clients/$id"
      params={{ id: client.userId }}
      title={client.name}
      data-testid="finance-client-link"
      // Inside a container that toggles on click (the mobile header): the
      // name navigates, it does not also expand the row.
      onClick={(event) => event.stopPropagation()}
      className="min-w-0 truncate hover:underline"
    >
      {client.name}
    </Link>
  );
}

/** ▾ / ▴ — the keyboard-reachable control that expands the client's invoices. */
function ToggleChevron({
  name,
  expanded,
  onToggle,
}: {
  name: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-label={t(expanded ? 'adminFinance.table.hideInvoices' : 'adminFinance.table.showInvoices', {
        name,
      })}
      data-testid="finance-client-toggle"
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      className="shrink-0 rounded-cf-sm px-1 font-normal text-cf-ink-subtle hover:text-cf-ink"
    >
      {expanded ? '▴' : '▾'}
    </button>
  );
}

/* ───────────────────────────── «Нагадати» ─────────────────────────────────── */

/** `11:32` today, `18.10 11:32` otherwise — a reminder can be up to 24 h old. */
function formatRemindedAt(date: Date, now: Date = new Date()): string {
  const time = new Intl.DateTimeFormat('uk-UA', { hour: '2-digit', minute: '2-digit' }).format(
    date,
  );
  return date.toDateString() === now.toDateString() ? time : `${formatDayMonth(date)} ${time}`;
}

/** The ISO instant in the server's `REMIND_TOO_SOON` message, if it names one. */
function nextReminderAt(error: unknown): Date | null {
  const match = /possible from (\d{4}-\d{2}-\d{2}T[\d:.]+Z)/.exec(financeErrorMessage(error) ?? '');
  if (!match?.[1]) return null;
  const at = new Date(match[1]);
  return Number.isNaN(at.getTime()) ? null : at;
}

/** A refused reminder → an EXPLANATION where the server gave a typed reason. */
function remindFailure(error: unknown): { text: string; info: boolean } {
  switch (refusalCode(error)) {
    case 'REMIND_TOO_SOON': {
      const next = nextReminderAt(error);
      return {
        text: next
          ? t('adminFinance.table.remindTooSoon', { time: formatRemindedAt(next) })
          : t('adminFinance.table.remindTooSoonNoTime'),
        info: true,
      };
    }
    case 'INVOICE_ALREADY_PAID':
      return { text: t('adminFinance.table.remindAlreadyPaid'), info: true };
    case 'PAYMENTS_DISABLED':
      return { text: t('adminFinance.table.remindPaymentsDisabled'), info: true };
    default:
      return { text: t('adminFinance.table.remindFailed'), info: false };
  }
}

interface RemindAction {
  /** The invoice the button reminds about; null = nothing remindable. */
  invoiceNo: string | null;
  /** Inside the 24 h throttle — the row says «нагадано …» instead. */
  remindedAt: Date | null;
  pending: boolean;
  failure: { text: string; info: boolean } | null;
  run: () => void;
}

/** One row's reminder: target invoice, throttle state and the write itself. */
function useRemindAction(client: FinanceClient): RemindAction {
  const queryClient = useQueryClient();
  const target = remindTarget(client);
  /** What THIS session just sent, until the refetched `remindedAt` carries it. */
  const [sent, setSent] = useState<{ no: string; at: Date } | null>(null);

  const mutation = useMutation({
    mutationFn: (no: string) => remindInvoice(no),
    onSuccess: (_data, no) => {
      setSent({ no, at: new Date() });
      void queryClient.invalidateQueries({ queryKey: FINANCE_CLIENTS_QUERY_KEY });
    },
    onError: (error) => {
      // The row no longer describes the invoice — re-read the balances.
      if (refusalCode(error) === 'INVOICE_ALREADY_PAID') {
        void queryClient.invalidateQueries({ queryKey: FINANCE_CLIENTS_QUERY_KEY });
        void queryClient.invalidateQueries({ queryKey: ['admin', 'finance', 'summary'] });
      }
    },
  });

  const local = sent && target && sent.no === target.no ? sent.at : null;
  const stored = target?.remindedAt ?? null;
  const latest = local && (!stored || local > stored) ? local : stored;

  return {
    invoiceNo: target?.no ?? null,
    remindedAt: recentReminder(latest),
    pending: mutation.isPending,
    failure: mutation.error ? remindFailure(mutation.error) : null,
    run: () => {
      if (!target || mutation.isPending) return;
      mutation.mutate(target.no);
    },
  };
}

/** The button, or «нагадано …», plus the explanation of a refusal. */
function RemindControl({
  remind,
  label,
  size,
}: {
  remind: RemindAction;
  label: string;
  size: 'h-8' | 'h-9';
}) {
  if (remind.remindedAt) {
    return (
      <span className="font-mono text-[11px] whitespace-nowrap text-cf-success">
        {t('adminFinance.table.reminded', { time: formatRemindedAt(remind.remindedAt) })}
      </span>
    );
  }
  const base = cn(
    size,
    'rounded-cf-md border border-cf-warn bg-cf-warn-tint px-3 text-[12px] font-semibold whitespace-nowrap text-cf-ink',
  );
  if (!remind.invoiceNo) {
    return (
      <button
        type="button"
        disabled
        title={t('adminFinance.table.remindNoInvoice')}
        className={cn(base, 'cursor-not-allowed opacity-40')}
      >
        {label}
      </button>
    );
  }
  return (
    <>
      <button
        type="button"
        // aria-disabled, not disabled: focus stays on the button while the call
        // is in flight (same as «Депозити й режими»).
        aria-disabled={remind.pending}
        title={t('adminFinance.table.remindHint', { no: remind.invoiceNo })}
        onClick={remind.run}
        className={cn(base, 'hover:border-cf-ink aria-disabled:cursor-wait aria-disabled:opacity-40')}
      >
        {remind.pending ? t('adminFinance.table.remindPending') : label}
      </button>
      {remind.failure && (
        <p
          role="alert"
          className={cn(
            'w-full max-w-[220px] text-right text-[11px] leading-[1.4] break-words',
            remind.failure.info ? 'text-cf-info-ink' : 'text-cf-danger',
          )}
        >
          {remind.failure.text}
        </p>
      )}
    </>
  );
}

/** One labelled figure in the mobile card's three-column band. */
function MobileFact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-mono text-[9px] tracking-[0.08em] text-cf-ink-subtle uppercase">
        {label}
      </div>
      <div className="mt-0.5 font-mono text-[13px]">{children}</div>
    </div>
  );
}
